/**
 * Estado do dashboard na URL (compartilhável) e cálculo do período.
 * Módulo leve e puro: é importado pelo router (bundle principal).
 *
 *   ?preset=30d&compare=true&f=["country:BR","path:/blog"]&interval=day
 *   ?preset=custom&from=2026-09-01&to=2026-09-30
 */
import { DIMENSIONS, type AnalyticsQuery, type Dimension, type Filters, type Interval } from "./types";

export const PRESETS = ["today", "7d", "30d", "90d", "12m", "custom"] as const;
export type Preset = (typeof PRESETS)[number];
export const BASIC_PRESETS: readonly Preset[] = ["today", "7d", "30d"];
export const DEFAULT_PRESET: Preset = "7d";

export const INTERVALS: readonly Interval[] = ["hour", "day", "week", "month"];

export type AnalyticsSearch = {
  preset?: Preset | undefined;
  /** YYYY-MM-DD (só com preset=custom; `to` inclusivo na interface). */
  from?: string | undefined;
  to?: string | undefined;
  compare?: boolean | undefined;
  /** Filtros "dimensão:valor". */
  f?: string[] | undefined;
  /** Agrupamento escolhido à mão (sem valor = automático). */
  interval?: Interval | undefined;
  /** Incluir robôs nas métricas de visitantes (padrão: excluídos). */
  bots?: boolean | undefined;
};

const MAX_FILTERS = 20;
const MAX_FILTER_VALUE = 500;
const YMD = /^\d{4}-\d{2}-\d{2}$/;

const isDimension = (v: string): v is Dimension => (DIMENSIONS as readonly string[]).includes(v);

/** Converte "dim:valor" → [dim, valor] (null se inválido). */
export function parseFilter(raw: string): [Dimension, string] | null {
  const i = raw.indexOf(":");
  if (i <= 0) return null;
  const dim = raw.slice(0, i);
  const value = raw.slice(i + 1);
  if (!isDimension(dim) || !value || value.length > MAX_FILTER_VALUE) return null;
  return [dim, value];
}

export const filterToken = (dim: Dimension, value: string) => `${dim}:${value}`;

function validYmd(v: unknown): string | undefined {
  if (typeof v !== "string" || !YMD.test(v)) return undefined;
  const d = new Date(`${v}T00:00:00Z`);
  return Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== v ? undefined : v;
}

/** validateSearch do router: tolera lixo na URL (valores inválidos são descartados). */
export function validateAnalyticsSearch(s: Record<string, unknown>): AnalyticsSearch {
  const preset = typeof s.preset === "string" && (PRESETS as readonly string[]).includes(s.preset) ? (s.preset as Preset) : undefined;
  const rawF = Array.isArray(s.f) ? s.f : typeof s.f === "string" ? [s.f] : [];
  const seen = new Set<string>();
  const f: string[] = [];
  for (const item of rawF) {
    if (typeof item !== "string" || f.length >= MAX_FILTERS) continue;
    const parsed = parseFilter(item);
    if (!parsed) continue;
    const token = filterToken(...parsed);
    if (seen.has(token)) continue;
    seen.add(token);
    f.push(token);
  }
  const compare = s.compare === true || s.compare === 1 || s.compare === "1" || s.compare === "true" ? true : undefined;
  const bots = s.bots === true || s.bots === 1 || s.bots === "1" || s.bots === "true" ? true : undefined;
  const interval = typeof s.interval === "string" && (INTERVALS as readonly string[]).includes(s.interval) ? (s.interval as Interval) : undefined;
  let from = preset === "custom" ? validYmd(s.from) : undefined;
  let to = preset === "custom" ? validYmd(s.to) : undefined;
  if (from && to && from > to) [from, to] = [to, from];
  return {
    preset,
    from,
    to,
    compare,
    f: f.length ? f : undefined,
    interval,
    bots,
  };
}

/** Filtros da URL → formato da API ({ country: ["BR"] }). */
export function filtersFromTokens(tokens: readonly string[] | undefined): Filters {
  const out: Filters = {};
  for (const token of tokens ?? []) {
    const parsed = parseFilter(token);
    if (!parsed) continue;
    const [dim, value] = parsed;
    (out[dim] ??= []).push(value);
  }
  return out;
}

// ── datas no fuso escolhido ────────────────────────────────

/** Fuso do navegador (fallback America/Sao_Paulo). */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Sao_Paulo";
  } catch {
    return "America/Sao_Paulo";
  }
}

type Ymd = { y: number; m: number; d: number };

function zonedParts(date: Date, tz: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { y: get("year"), m: get("month"), d: get("day"), h: get("hour") % 24, min: get("minute"), s: get("second") };
}

/** Diferença (ms) entre o relógio do fuso e o UTC naquele instante. */
function tzOffsetMs(date: Date, tz: string): number {
  const p = zonedParts(date, tz);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

/** Meia-noite de uma data civil no fuso → instante UTC. */
export function zonedMidnight({ y, m, d }: Ymd, tz: string): Date {
  const guess = Date.UTC(y, m - 1, d);
  const off = tzOffsetMs(new Date(guess), tz);
  let ts = guess - off;
  const off2 = tzOffsetMs(new Date(ts), tz);
  if (off2 !== off) ts = guess - off2;
  return new Date(ts);
}

/** Soma dias a uma data civil (aritmética em UTC, sem horário de verão). */
export function addDays({ y, m, d }: Ymd, days: number): Ymd {
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

export const ymdToString = ({ y, m, d }: Ymd) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
export function ymdFromString(s: string): Ymd {
  const [y, m, d] = s.split("-").map(Number);
  return { y: y ?? 1970, m: m ?? 1, d: d ?? 1 };
}

export function todayIn(tz: string, now = new Date()): Ymd {
  const p = zonedParts(now, tz);
  return { y: p.y, m: p.m, d: p.d };
}

export type ResolvedRange = {
  preset: Preset;
  from: string;
  to: string;
  /** Dias civis cobertos (to exclusivo). */
  days: number;
  autoInterval: Interval;
  interval: Interval;
  /** Datas civis para os inputs (to inclusivo). */
  fromDay: string;
  toDay: string;
};

/** Agrupamento automático pelo tamanho do período. */
export function autoInterval(days: number, preset?: Preset): Interval {
  if (preset === "today" || days <= 2) return "hour";
  if (preset === "12m") return "month";
  if (days <= 92) return "day";
  if (days <= 200) return "week";
  return "month";
}

/** Agrupamentos que fazem sentido para o tamanho do período (evita milhares de pontos). */
export function allowedIntervals(days: number): Interval[] {
  return INTERVALS.filter((i) => (i === "hour" ? days <= 14 : i === "month" ? days >= 28 : i === "week" ? days >= 7 : true));
}

/**
 * Preset (ou datas personalizadas) → from/to ISO no fuso `tz` (to exclusivo,
 * meia-noite do dia seguinte ao último dia). Presets não liberados viram 7d.
 */
export function resolveRange(search: AnalyticsSearch, tz: string, allowed: readonly Preset[] = PRESETS, now = new Date()): ResolvedRange {
  let preset: Preset = search.preset && allowed.includes(search.preset) ? search.preset : DEFAULT_PRESET;
  if (preset === "custom" && !(search.from && search.to)) preset = DEFAULT_PRESET;
  const today = todayIn(tz, now);
  let first: Ymd;
  let last: Ymd = today;
  switch (preset) {
    case "today":
      first = today;
      break;
    case "30d":
      first = addDays(today, -29);
      break;
    case "90d":
      first = addDays(today, -89);
      break;
    case "12m": {
      // Do 1º dia do mês, 11 meses atrás, até hoje (12 barras mensais).
      const dt = new Date(Date.UTC(today.y, today.m - 1 - 11, 1));
      first = { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: 1 };
      break;
    }
    case "custom":
      first = ymdFromString(search.from!);
      last = ymdFromString(search.to!);
      break;
    default:
      first = addDays(today, -6);
  }
  const end = addDays(last, 1);
  const days = Math.round((Date.UTC(end.y, end.m - 1, end.d) - Date.UTC(first.y, first.m - 1, first.d)) / 86_400_000);
  const auto = autoInterval(days, preset);
  const interval = search.interval && allowedIntervals(days).includes(search.interval) ? search.interval : auto;
  return {
    preset,
    from: zonedMidnight(first, tz).toISOString(),
    to: zonedMidnight(end, tz).toISOString(),
    days,
    autoInterval: auto,
    interval,
    fromDay: ymdToString(first),
    toDay: ymdToString(last),
  };
}

/** Monta a AnalyticsQuery da API a partir da URL (respeitando o plano). */
export function buildQuery(
  search: AnalyticsSearch,
  opts: { tz: string; advanced: boolean; now?: Date },
): { query: AnalyticsQuery; range: ResolvedRange } {
  const range = resolveRange(search, opts.tz, opts.advanced ? PRESETS : BASIC_PRESETS, opts.now);
  const query: AnalyticsQuery = { from: range.from, to: range.to, tz: opts.tz, interval: range.interval };
  if (opts.advanced) {
    if (search.compare) query.compare = true;
    if (search.bots) query.includeBots = true;
    const filters = filtersFromTokens(search.f);
    if (Object.keys(filters).length) query.filters = filters;
  }
  return { query, range };
}
